import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContextService, TokenService } from '@maximizer/core/shared/data-access';
import { Octopus } from '@maximizer/core/shared/domain';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { Observable, catchError, of, switchMap, throwError } from 'rxjs';
import {
  INTEGRATION_IQ_BOOST_URL,
  INTEGRATION_WEB_APP_URL,
  LANGGRAPH_CONFIG,
} from '../types/injection-token';

const INVALID_TOKEN_MESSAGE = 'invalid token';

/**
 * Auth for IQ Boost BFF and LangGraph requests: sets bearer, refreshes once on 401 / invalid-token.
 * Registered only where the integration config is provided, so never in core.
 */
@Injectable()
export class AiIntegrationAuthInterceptor implements HttpInterceptor {
  private readonly context = inject(ContextService);
  private readonly token = inject(TokenService);
  private readonly baseUrls: readonly string[] = [
    `${inject(INTEGRATION_WEB_APP_URL) ?? ''}${inject(INTEGRATION_IQ_BOOST_URL) ?? ''}`,
    inject(LANGGRAPH_CONFIG, { optional: true })?.url ?? '',
  ].filter((url) => !isNullOrEmpty(url));

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isIntegrationRequest(request)) {
      return next.handle(request);
    }

    return this.attempt(request, next).pipe(
      catchError((error: unknown) => {
        if (!this.isAuthFailure(error)) {
          return throwError(() => error);
        }

        this.context.token = '';
        return this.token.getToken().pipe(
          catchError(() => throwError(() => error)),
          switchMap((token) =>
            isNullOrEmpty(token)
              ? throwError(() => error)
              : this.attempt(request, next),
          ),
        );
      }),
    );
  }

  private attempt(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(this.withAuthorization(request)).pipe(
      switchMap((event) =>
        event.type === HttpEventType.Response &&
        this.isInvalidAuthentication(event)
          ? throwError(
              () =>
                new HttpErrorResponse({
                  status: HttpStatusCode.Unauthorized,
                  error: (event as HttpResponse<unknown>).body,
                }),
            )
          : of(event),
      ),
    );
  }

  private withAuthorization(request: HttpRequest<unknown>): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${this.context.token || ''}` },
    });
  }

  private isIntegrationRequest(request: HttpRequest<unknown>): boolean {
    return this.baseUrls.some((url) => request.url.startsWith(url));
  }

  private isInvalidAuthentication(event: HttpEvent<unknown>): boolean {
    const body = (event as HttpResponse<unknown>).body as Octopus.Response;
    return this.isInvalidAuthenticationResponse(body);
  }

  private isAuthFailure(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    if (error.status === HttpStatusCode.Unauthorized) {
      return true;
    }
    return this.isInvalidAuthenticationResponse(error.error as Octopus.Response);
  }

  /**
   * Octopus is not always consistent with token errors: it may return
   * `InvalidAuthentication` (-2), or `Unsuccessful` (-1) with an "invalid token"
   * message. Both mean the bearer token is no longer valid.
   */
  private isInvalidAuthenticationResponse(
    response: Octopus.Response | null | undefined,
  ): boolean {
    if (!response) {
      return false;
    }

    if (response.Code === Octopus.ResponseStatusCode.InvalidAuthentication) {
      return true;
    }

    if (response.Code !== Octopus.ResponseStatusCode.Unsuccessful) {
      return false;
    }

    const messages: Array<string | Octopus.ResponseError> = response.Msg ?? [];
    return messages.some((error) => {
      const text = typeof error === 'string' ? error : error?.Message;
      return text?.toLowerCase().includes(INVALID_TOKEN_MESSAGE) ?? false;
    });
  }
}
